import { motion } from "framer-motion";
import { Star, ArrowUpRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { CERTS, GOOGLE_PROFILE_URL, GOOGLE_RATING, GOOGLE_REVIEWS } from "@/lib/site-data";

const ease = [0.22, 1, 0.36, 1];

const GoogleMark = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
  </svg>
);

const Stars = ({ rating, size = "h-5 w-5" }) => (
  <div className="flex gap-1">
    {[...Array(5)].map((_, s) => (
      <Star
        key={s}
        className={`${size} drop-shadow-[0_0_6px_rgba(255,201,41,0.5)] ${
          s < rating ? "fill-[#FFC929] text-[#FFC929]" : "fill-white/10 text-white/10"
        }`}
      />
    ))}
  </div>
);

export default function Trust() {
  return (
    <section data-testid="trust-section" className="relative py-28 md:py-36">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading
          eyebrow="Confiance & garanties"
          title="Des certifications qui engagent"
          copy="Chaque chantier est couvert, chaque artisan est qualifié. Vous signez en toute sérénité."
        />

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {CERTS.map((c, i) => (
            <motion.div
              key={c.id}
              data-testid={`cert-${c.id}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08, duration: 0.6, ease }}
              className="border border-white/5 bg-[#14171D] p-7 transition-colors duration-300 hover:border-terra/40"
            >
              <c.icon className="h-7 w-7 text-terra" />
              <h3 className="mt-5 font-serif text-lg text-slate-100">{c.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-slate-400">{c.text}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          data-testid="google-reviews-block"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease }}
          className="mt-20 border border-white/5 bg-[#14171D] p-7 md:p-10"
        >
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="flex h-14 w-14 items-center justify-center rounded-full border border-white/10 bg-white">
                <GoogleMark className="h-7 w-7" />
              </div>
              <div>
                <p className="font-serif text-lg text-slate-100">Avis Google</p>
                <div className="mt-1 flex items-center gap-2.5">
                  <span data-testid="google-rating-score" className="font-serif text-3xl font-semibold text-white">{GOOGLE_RATING.score}</span>
                  <Stars rating={Math.round(parseFloat(GOOGLE_RATING.score.replace(",", ".")))} size="h-6 w-6" />
                  <span className="text-xs text-slate-500">· {GOOGLE_RATING.count} avis</span>
                </div>
              </div>
            </div>
            <a
              href={GOOGLE_PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-profile-link"
              className="group inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-xs font-semibold uppercase tracking-widest text-slate-200 transition-colors duration-300 hover:border-terra hover:text-terra"
            >
              Voir notre fiche Google
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </div>

          <div className="mt-8 grid gap-5 lg:grid-cols-3">
            {GOOGLE_REVIEWS.map((r, i) => (
              <motion.figure
                key={r.id}
                data-testid={`google-review-${r.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.08, duration: 0.55, ease }}
                className="flex flex-col border border-white/5 bg-[#0D0F12] p-6"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-terra/15 font-serif text-sm text-terra">
                      {r.name.charAt(0)}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-white">{r.name}</p>
                      <p className="mt-0.5 text-[11px] text-slate-500">{r.date} · {r.lieu}</p>
                    </div>
                  </div>
                  <GoogleMark className="h-4 w-4 shrink-0 opacity-70" />
                </div>
                <div className="mt-4">
                  <Stars rating={r.rating} />
                </div>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-slate-300">{r.text}</blockquote>
              </motion.figure>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
